import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  SchedulingJobWeekStatusResponse,
  StaffResponse,
  YearPlanResponse
} from '../../api/generated';
import type { WeekCalculationStatuses } from '../../hooks/useWeekCalculationStatuses';
import { useHasPermission } from '../../hooks/useHasPermission';
import { useStaffCompetences } from '../../hooks/useStaffCompetences';
import { PERMISSIONS } from '../../constants/permissions';
import { calculateWeeklyCompetenceCapacityHours } from '../../services/staffCapacityService';
import { formatDateStr } from '../../utils/dateUtils';
import type { StatusChipAction } from './WeekKpiCell/weekCalculationState';
import {
  resolveStatusChipAction,
  resolveWeekCalculationState
} from './WeekKpiCell/weekCalculationState';
import { StatusRow } from './WeekKpiCell/StatusRow';
import { ProfessionHoursRow } from './WeekKpiCell/ProfessionHoursRow';
import { PatientsBedsRow } from './WeekKpiCell/PatientsBedsRow';
import { WeekWarningIcon } from './WeekKpiCell/WeekWarningIcon';
import type { BedContext } from './WeekKpiCell/computeWeekKpi';
import { UNSCHEDULED_UNKNOWN, computeWeekKpi } from './WeekKpiCell/computeWeekKpi';
import type { UnscheduledCountState } from './WeekKpiCell/types';
import { OpenCalendarButton } from '../UI';
import styles from './GanttWeekHeader.module.css';

interface GanttWeekHeaderCornerProps {
  hasCalendarRow?: boolean;
}

export const GanttWeekHeaderCorner = ({ hasCalendarRow = false }: GanttWeekHeaderCornerProps) => {
  const { t } = useTranslation();

  return (
    <div className={`${styles.corner} ${hasCalendarRow ? styles.cornerTall : ''}`}>
      <div className={styles.cornerLabel} />
      <div className={styles.cornerKpi}>
        <div className={styles.cornerKpiRow}>{t('gantt-status')}</div>
        <div className={styles.cornerKpiRowDouble}>{t('gantt-employee-hours')}</div>
        <div className={styles.cornerKpiRow}>{t('gantt-patients-beds')}</div>
      </div>
      {hasCalendarRow && <div className={styles.cornerCalendarSpacer} />}
    </div>
  );
};

interface GanttWeekHeaderCellsProps {
  yearPlan: YearPlanResponse;
  showKpi?: boolean;
  onWeekStatusClick?: (weekNumber: number) => void;
  onOpenCalendar?: (weekNumber: number) => void;
  // Retries loading the statuses. Offered on UNKNOWN weeks in place of a recalculation.
  onRetryCalculationStatuses?: () => void;
  // Omitted by callers that render the header without KPIs, hence the no-statuses default below.
  weekCalculations?: WeekCalculationStatuses;
  // What the solver could not place, per week. A week is absent until its result has been read, so
  // the unplanned tag stays hidden rather than claiming a clean zero for a week nobody has looked at.
  // Opens the list of unplanned interventions for a week. Without it the tag renders as a plain
  // (unclickable) count.
  onOpenUnscheduled?: (weekNumber: number) => void;
  // The location's settled staff roster, the source of each week's capacity.
  // Undefined while it has not loaded (or failed to): the KPI then omits
  // capacity thresholds instead of judging planned hours against a guess.
  staff?: readonly StaffResponse[];
  // The location's patient rooms and the booked patients' needs, the ceiling on
  // a week's admitted patients (see useBedContext). Undefined until the room
  // list has loaded, on the same terms as `staff`: the bed chip then reports
  // demand without reddening.
  beds?: BedContext;
}

// `StatusRow`'s click prop: the week number is passed to every handler, and one of them ignores it.
type StatusChipClickHandler = (weekNumber: number) => void;

// The backend omits the count until the week's current job has a result, which is not the same as a
// week with nothing unplaced: a week being recalculated reports none, because the previous solve's
// leftovers belong to a plan the week is no longer trying to place. Converted explicitly rather than
// by reaching for `?? 0`, so the distinction survives the hop into `computeWeekKpi` instead of
// collapsing into a false clean week.
const resolveUnscheduledState = (
  status: SchedulingJobWeekStatusResponse | undefined
): UnscheduledCountState =>
  status?.unscheduledCount === undefined
    ? UNSCHEDULED_UNKNOWN
    : { kind: 'known', count: status.unscheduledCount };

// Nothing fetched and nothing attempted: every status chip stays hidden, which is what the
// KPI-less preview header wants.
const NO_WEEK_CALCULATIONS: WeekCalculationStatuses = {
  statusByWeek: new Map(),
  hasAttempted: false,
  isUnavailable: false
};

export const GanttWeekHeaderCells = ({
  yearPlan,
  showKpi = true,
  onWeekStatusClick,
  onOpenCalendar,
  onRetryCalculationStatuses,
  weekCalculations = NO_WEEK_CALCULATIONS,
  onOpenUnscheduled,
  staff,
  beds
}: GanttWeekHeaderCellsProps) => {
  const { t } = useTranslation();
  const { competences } = useStaffCompetences();
  const canExecuteCalculation = useHasPermission(PERMISSIONS.AlgorithmJobsExecute);
  const weekEntries = useMemo(
    () =>
      yearPlan.weeks.map((week) => {
        const status = weekCalculations.statusByWeek.get(week.weekNumber);
        return {
          week,
          kpi: computeWeekKpi(
            yearPlan,
            week.weekNumber,
            competences,
            resolveWeekCalculationState(status, weekCalculations.isUnavailable),
            staff && calculateWeeklyCompetenceCapacityHours(staff, week),
            resolveUnscheduledState(status),
            beds
          )
        };
      }),
    [yearPlan, competences, weekCalculations, staff, beds]
  );

  // What each chip action does, in one exhaustive table: a new `StatusChipAction` fails to typecheck
  // until it is handled here. The retry handler takes no week number, which a `(week: number) => void`
  // slot accepts, so both actions fit `StatusRow`'s single click prop.
  const statusClickHandlers: Record<StatusChipAction, StatusChipClickHandler | undefined> = {
    none: undefined,
    // Reloading the statuses is a read, so it stays offered to everyone who can
    // see the plan.
    retry: onRetryCalculationStatuses,
    // Queueing a solve is ALGORITHM_JOBS_EXECUTE on the backend. Without the gate a
    // therapist's chip still invited the click and answered with a 403 dialog,
    // so for them the chip is a plain status label: no handler means no button,
    // and `StatusRow` drops the "click to recalculate" hint with it.
    recalculate: canExecuteCalculation ? onWeekStatusClick : undefined
  };

  // The compact preview has no room for the row, so the two flags are combined
  // once here rather than left to agree by convention.
  const hasCalendarRow = showKpi && onOpenCalendar !== undefined;

  return (
    <div
      className={`${styles.headerCells} ${showKpi ? '' : styles.headerCellsCompact} ${hasCalendarRow ? styles.headerCellsTall : ''}`}
    >
      {weekEntries.map(({ week, kpi }) => {
        // The chip's wording comes from `StatusRow`'s presentation table, keyed on the same status,
        // so the label and the behaviour cannot disagree.
        const onStatusClick = statusClickHandlers[resolveStatusChipAction(kpi.calculation.status)];
        return (
          <div key={week.weekNumber} className={styles.weekCell}>
            <div className={styles.weekLabel}>
              <span className={styles.weekNumberAnchor}>
                <span className={styles.weekNumber}>U{week.weekNumber}</span>
                {showKpi && <WeekWarningIcon kpi={kpi} />}
              </span>
              {showKpi && (
                <span className={styles.weekRange}>
                  {formatDateStr(week.startDate)} - {formatDateStr(week.endDate)}
                </span>
              )}
            </div>
            {showKpi && (
              <div className={styles.weekKpi}>
                <div className={styles.weekKpiRow}>
                  {/* Every chip waits for the first fetch to settle rather than flashing "not
                      calculated". Settled, not succeeded: if the fetch failed the chip still appears
                      as UNKNOWN, because hiding it would silently remove the planner's only way in.
                      Started weeks used to render straight away on calendar knowledge alone, which
                      REH-358 removed along with the locked status. */}
                  {weekCalculations.hasAttempted && (
                    <StatusRow
                      kpi={kpi}
                      onStatusClick={onStatusClick}
                      onPlanningIssueClick={onOpenUnscheduled}
                    />
                  )}
                </div>
                <div className={styles.weekKpiRowDouble}>
                  <ProfessionHoursRow professions={kpi.professions} />
                </div>
                <div className={styles.weekKpiRow}>
                  <PatientsBedsRow kpi={kpi} />
                </div>
              </div>
            )}
            {hasCalendarRow && (
              <span className={styles.openCalendarSlot}>
                <OpenCalendarButton
                  label={t('week-overview-open-calendar-week', {
                    weekNumber: week.weekNumber
                  })}
                  onClick={() => onOpenCalendar(week.weekNumber)}
                />
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};
